/*
 * A reader's search, split into the words it is matched by (2026-10-13).
 *
 * ONE RULE, THREE READERS. Both public searches - the article list and the
 * release-note list - match a search word by word, and the browse page has to
 * know before the server answers whether a search will be ranked, because only
 * a PLAIN word reaches the full-text rank. All three ask this function, so the
 * page's "Best match" cannot claim an order the server did not produce, and the
 * two searches cannot drift apart in what they call a word.
 *
 * THE RULE. The search is normalised to NFC - "på" typed as "pa" plus a
 * combining ring is the same word as the precomposed one - and split on
 * whitespace. Each word loses its EDGE punctuation: anything that is not a
 * letter, number or combining mark (\p{L}\p{N}\p{M}) at either end - quotes,
 * brackets, a trailing comma or full stop, the "#" of "C#". A word with nothing
 * left is dropped, and so is one with no letter or number in it - marks alone,
 * like the invisible variation selector a pasted emoji carries ("⚠️ Warning"):
 * the index would get a term its word breaker reduces to nothing, which in an
 * AND finds nothing at all, and a LIKE treats it as matching everything (review,
 * 2026-10-15). Only the first SEARCH_MAX_WORDS are kept. What remains is
 *
 *   PLAIN       letters, numbers and combining marks only - "locking", "på",
 *               "0x80070005". The full-text index can take it as a prefix term.
 *   PUNCTUATED  anything else inside it - "v1.1", "1.1.2.5", "e-mail", "node.js",
 *               "don't". The reader means those characters, so the server
 *               matches the word AS TYPED, as a substring; and, where the index
 *               is ready, also its STRIPPED form - every character outside
 *               \p{L}\p{N}\p{M} removed, "v11", "email" - as a prefix term, so
 *               nothing the full-text search used to find for it is lost.
 *
 * Every word must match, anywhere: the server ANDs them.
 *
 * WHY (the owner's report, 2026-10-13: "The search for "v1.1" only returns one
 * result instead of them both. All matches should be returned, as for all other
 * searches."). TEST has two live accoPLANNING notes whose titles read v1.1.2.5
 * and v1.1.2.1. Every character that was not a letter or digit used to be
 * dropped from a search word, so the full-text index was asked for "v11*" - a
 * word no text holds - and only the note whose VERSION matched was found. The
 * first fix searched any search with punctuation in it as one whole substring,
 * which lost "accoPLANNING v1.1" (no text holds that phrase) and the joined form
 * of "e-mail"; this rule replaced it the same day.
 *
 * A pure function of the string - no database, no DOM - so the server and the
 * web client get the same answer for the same text.
 */
/** More words than this and the reader is pasting, not searching. */
export const SEARCH_MAX_WORDS = 8;
/** Anything that is not a letter, number or combining mark, at either end of a word. */
const EDGE_PUNCTUATION = /^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu;
/** One character that is not a letter, number or combining mark. */
const NOT_WORD_CHARACTER = /[^\p{L}\p{N}\p{M}]/u;
/** Every run of them. */
const NOT_WORD_CHARACTERS = /[^\p{L}\p{N}\p{M}]+/gu;
/** A letter or a number - a word needs one to be searchable. */
const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;
/**
 * The words of a search, by the rule above: at most SEARCH_MAX_WORDS of them,
 * in the order typed, none empty. Empty for no search, a blank one, or one made
 * of punctuation alone ("***", "#").
 */
export function splitSearchWords(search) {
    if (!search)
        return [];
    const words = [];
    for (const raw of search.normalize('NFC').trim().split(/\s+/)) {
        const word = raw.replace(EDGE_PUNCTUATION, '');
        if (word.length === 0 || !LETTER_OR_NUMBER.test(word))
            continue;
        const plain = !NOT_WORD_CHARACTER.test(word);
        words.push({ word, plain, stripped: plain ? word : word.replace(NOT_WORD_CHARACTERS, '') });
        if (words.length === SEARCH_MAX_WORDS)
            break;
    }
    return words;
}
//# sourceMappingURL=search.js.map